/* Motor příležitostí ke komunikaci — JEDEN seznam „co dnes poslat".
 *
 * Čistá funkce nad daty, která appka už má (kontakty, smlouvy z
 * /api/contracts/upcoming-fixations, položky diáře, partner + děti z analýz).
 * Žádný React, žádné API — testovatelné samostatně.
 *
 * Parametry pravidel (dny předem, okna, priority, výchozí kanál) jsou v
 * pravidla.json; tenhle soubor je jen vykonává. Backend má kopii JSON a pro
 * automatické e-maily počítá jen druhy `rocne` a `jednorazove` (viz
 * backend/routes/komunikace.py) — stejné parametry, stejný výsledek.
 *
 * NEZNÁMÉ ≠ 0: bez data žádná událost. Vadné pole (null, text, NaN) se přeskočí.
 *
 * Tvar události:
 *   { id, typ, druh, datum (YYYY-MM-DD), dny (do data; záporné = po termínu),
 *     kdy (0 = jednat dnes, 1–7 = tento týden), pripominka (bool),
 *     kontaktId, jmeno, osoba, produkt, telefon, email, souhlas, priorita,
 *     kanal, sablona }
 *
 * Servisní radar (lib/servisRadar.js) hlídá termíny 120 dní dopředu pro
 * zvoneček; tady jde o KONKRÉTNÍ zprávu klientovi s šablonou a kanálem —
 * proto vlastní okna (fixace 6 měsíců předem, narozeniny D-3/D-0).
 */
import PRAVIDLA from "./pravidla.json";

export { PRAVIDLA };

const DEN_MS = 86400000;

/** Pole objektů, nebo prázdné pole — API i localStorage vrací cokoli. */
const pole = (v) => (Array.isArray(v) ? v.filter((x) => x && typeof x === "object") : []);

/** „YYYY-MM-DD" → { r, m, d } nebo null (prázdno, text, nesmysl → null). */
export function rozlozDatum(v) {
  if (v instanceof Date) {
    return isNaN(v.getTime()) ? null : { r: v.getFullYear(), m: v.getMonth() + 1, d: v.getDate() };
  }
  if (typeof v !== "string") return null;
  const m = v.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const out = { r: +m[1], m: +m[2], d: +m[3] };
  if (out.m < 1 || out.m > 12 || out.d < 1 || out.d > 31) return null;
  // 31. 4., 30. 2. … → neplatné (Date by to tiše přetočilo do dalšího měsíce)
  if (new Date(Date.UTC(out.r, out.m - 1, out.d)).getUTCDate() !== out.d) return null;
  return out;
}

const iso = ({ r, m, d }) => `${String(r).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const utc = ({ r, m, d }) => Date.UTC(r, m - 1, d);
const prestupny = (r) => (r % 4 === 0 && r % 100 !== 0) || r % 400 === 0;

/** Celé dny od `dnes` do `datum` (oba { r, m, d }); záporné = minulost. */
export const dnyMezi = (dnes, datum) => Math.round((utc(datum) - utc(dnes)) / DEN_MS);

/** Posune datum o `roky` let; 29. 2. v nepřestupném roce → 28. 2. */
export function posunRoky(datum, roky) {
  const r = datum.r + roky;
  const d = datum.m === 2 && datum.d === 29 && !prestupny(r) ? 28 : datum.d;
  return { r, m: datum.m, d };
}

/**
 * Nejbližší výskyt výročí (měsíc + den) k dnešku — letošní, když ještě
 * neproběhl (včetně dneška a `tolerancePo` dní zpět), jinak příští rok.
 * Vrací { datum, rocnik } — ročník = kolikáté výročí (věk u narozenin).
 */
export function dalsiVyskyt(puvod, dnes, tolerancePo = 0) {
  let kandidat = posunRoky(puvod, dnes.r - puvod.r);
  if (dnyMezi(dnes, kandidat) < -tolerancePo) kandidat = posunRoky(puvod, dnes.r - puvod.r + 1);
  return { datum: kandidat, rocnik: kandidat.r - puvod.r };
}

const jmenoKontaktu = (c) => [c.name, c.surname].filter((x) => typeof x === "string" && x.trim()).join(" ").trim() || (typeof c.company === "string" ? c.company : "") || "";
const faze = (c) => (typeof c.stage === "string" ? c.stage : "");
const jeKlient = (c) => PRAVIDLA.fazeKlienta.includes(faze(c));
const jeLead = (c) => PRAVIDLA.fazeLeadu.includes(faze(c));

/** Kontakty, kterým se vůbec smí psát: ne koš, ne nábor, ne „nekontaktovat". */
function kontaktovatelne(contacts) {
  return pole(contacts).filter((c) => c.id && !c.archived && !c.is_recruit && !c.nekontaktovat);
}

/** Nastavení typu po sloučení s výchozími hodnotami z pravidel. */
export function nastaveniTypu(nastaveni, typ) {
  const p = PRAVIDLA.typy[typ] || {};
  const n = (nastaveni && nastaveni.typy && nastaveni.typy[typ]) || {};
  const cislo = (v, vychozi) => (Number.isFinite(Number(v)) && v !== "" && v !== null ? Math.max(0, Math.min(365, Number(v))) : vychozi);
  return {
    zapnuto: n.zapnuto === undefined ? true : !!n.zapnuto,
    dniPredem: cislo(n.dniPredem, p.dniPredem ?? p.pripominkaDni ?? 0),
    kanal: ["whatsapp", "sms", "email"].includes(n.kanal) ? n.kanal : (p.kanal || "whatsapp"),
  };
}

/**
 * Hlavní funkce. Vrací události seřazené: dnes → tento týden, uvnitř podle
 * priority (vysoká první) a jména.
 *
 * @param {object} vstup
 * @param {Array}  vstup.contacts   /api/crm/contacts
 * @param {Array}  vstup.fixations  /api/contracts/upcoming-fixations
 * @param {Array}  vstup.tasks      /api/crm/tasks (rezervace mají source from_booking)
 * @param {Array}  vstup.osoby      /api/komunikace/osoby → [{ contact_id, partner, deti }]
 * @param {object} vstup.nastaveni  /api/komunikace/nastaveni (per poradce)
 * @param {Array}  vstup.odeslano   /api/komunikace/odeslano → [{ kontakt_id, typ, datum, kdy }]
 * @param {Date|string} vstup.dnes  pro testy; výchozí = teď
 */
export function udalostiKomunikace({ contacts, fixations, tasks, osoby, nastaveni, odeslano, dnes } = {}) {
  const dnesD = rozlozDatum(dnes instanceof Date ? dnes : (dnes ? String(dnes) : new Date()));
  if (!dnesD) return [];
  const out = [];
  const kontakty = kontaktovatelne(contacts);
  const podleId = new Map(kontakty.map((c) => [String(c.id), c]));
  const osobyPodleId = new Map(pole(osoby).map((o) => [String(o.contact_id), o]));
  const odeslanoList = pole(odeslano);

  const zakladni = (c) => ({
    kontaktId: String(c.id), jmeno: jmenoKontaktu(c), osoba: "", produkt: "",
    telefon: typeof c.phone === "string" ? c.phone : "", email: typeof c.email === "string" ? c.email : "",
    souhlas: !!c.consent_given,
  });

  /* Už odbaveno / odesláno? Termínové typy podle (kontakt, typ, datum);
     druh ticho podle (kontakt, typ) mladší než poDnech — ať nenaléhá hned znovu. */
  const uzOdeslano = (typ, kontaktId, datumIso, druh) => odeslanoList.some((o) => {
    if (String(o.kontakt_id) !== kontaktId || o.typ !== typ) return false;
    if (druh !== "ticho") return o.datum === datumIso;
    const kdy = rozlozDatum(o.kdy || o.datum);
    const po = (PRAVIDLA.typy[typ] || {}).poDnech || 0;
    return !!kdy && dnyMezi(kdy, dnesD) < po;
  });

  const pridej = (typ, c, datum, extra = {}) => {
    const p = PRAVIDLA.typy[typ];
    const n = nastaveniTypu(nastaveni, typ);
    if (!p || !n.zapnuto) return;
    const datumIso = iso(datum);
    if (uzOdeslano(typ, String(c.id), datumIso, p.druh)) return;
    const dny = dnyMezi(dnesD, datum);
    out.push({
      id: `${typ}-${c.id}-${datumIso}${extra.pripominka ? "-p" : ""}${extra.klic ? `-${extra.klic}` : ""}`,
      typ, druh: p.druh, datum: datumIso, dny, kdy: extra.kdy ?? Math.max(0, dny), pripominka: !!extra.pripominka,
      ...zakladni(c), priorita: extra.priorita || p.priorita, kanal: n.kanal, sablona: extra.sablona || typ,
      ...(extra.osoba ? { osoba: extra.osoba } : {}), ...(extra.produkt ? { produkt: extra.produkt } : {}),
      ...(extra.text ? { text: extra.text } : {}),
    });
  };

  /* Roční výročí: hlavní událost (D-0, tolerance poTerminuDni zpět) + připomínka D-n. */
  const rocne = (typ, c, puvodIso, extra = {}) => {
    const puvod = rozlozDatum(puvodIso);
    if (!puvod) return;
    const n = nastaveniTypu(nastaveni, typ);
    const { datum, rocnik } = dalsiVyskyt(puvod, dnesD, PRAVIDLA.poTerminuDni);
    if (rocnik <= 0) return; // datum založení dnes = žádné výročí
    const dny = dnyMezi(dnesD, datum);
    const kulatiny = typ === "narozeniny" && rocnik % PRAVIDLA.kulatinyKazdych === 0;
    const spolecne = { ...extra, rocnik, ...(kulatiny ? { sablona: "kulatiny", priorita: "vysoka" } : {}) };
    // Den připomínky (D-n) je sám připomínkou — náhled „za n dní" se ten den nepřidává dvakrát.
    if (n.dniPredem > 0 && dny === n.dniPredem) pridej(typ, c, datum, { ...spolecne, pripominka: true, kdy: 0 });
    else if (dny <= 7) pridej(typ, c, datum, { ...spolecne, kdy: Math.max(0, dny) });
  };

  /* Jednorázové datum s oknem: jednat od (datum − dniPredem) do data. */
  const jednorazove = (typ, c, datumIso, extra = {}) => {
    const datum = rozlozDatum(datumIso);
    if (!datum) return;
    const n = nastaveniTypu(nastaveni, typ);
    const dny = dnyMezi(dnesD, datum);
    if (dny < -PRAVIDLA.poTerminuDni || dny > n.dniPredem) return;
    pridej(typ, c, datum, { ...extra, kdy: 0 });
  };

  kontakty.forEach((c) => {
    rocne("narozeniny", c, c.dob || c.narozeni);
    if (jeKlient(c)) rocne("vyroci_spoluprace", c, c.created_at);
    jednorazove("servis", c, c.servis_next_date);

    const o = osobyPodleId.get(String(c.id));
    if (o) {
      const partner = o.partner && typeof o.partner === "object" ? o.partner : null;
      if (partner && partner.narozeni) rocne("narozeniny_partner", c, partner.narozeni, { osoba: typeof partner.jmeno === "string" ? partner.jmeno : "" });
      pole(o.deti).forEach((dite, i) => {
        const nar = rozlozDatum(dite.narozeni);
        if (!nar) return;
        jednorazove("dite_18", c, iso(posunRoky(nar, PRAVIDLA.typy.dite_18.posunRoku)), { osoba: typeof dite.jmeno === "string" ? dite.jmeno : "", klic: `d${i}` });
      });
    }

    // Lead, který N dní mlčí (od příchodu leadu). Jakákoli odbavená zpráva to na N dní ztiší.
    if (jeLead(c)) {
      const prisel = rozlozDatum(c.lead_at || c.created_at);
      const p = PRAVIDLA.typy.lead_bez_odpovedi;
      if (prisel && dnyMezi(prisel, dnesD) >= p.poDnech) pridej("lead_bez_odpovedi", c, dnesD, { kdy: 0 });
    }

    // Klient, se kterým nebyl kontakt N dní. Poslední kontakt = last_contact_at, jinak updated_at (přibližné).
    if (jeKlient(c)) {
      const posledni = rozlozDatum(c.last_contact_at || c.updated_at);
      const p = PRAVIDLA.typy.dlouho_bez_kontaktu;
      if (posledni && dnyMezi(posledni, dnesD) >= p.poDnech) pridej("dlouho_bez_kontaktu", c, dnesD, { kdy: 0 });
    }
  });

  // Smlouvy: konec fixace 6 měsíců předem, výročí smlouvy 14 dní předem.
  pole(fixations).forEach((f) => {
    const c = podleId.get(String(f.contact_id));
    if (!c) return;
    const produkt = typeof f.contract_type === "string" ? f.contract_type : "";
    const klic = String(f.id || f.contract_id || "");
    if (f.fixation_end) jednorazove("fixace", c, f.fixation_end, { produkt, klic });
    if (f.anniversary_date) jednorazove("vyroci_smlouvy", c, f.anniversary_date, { produkt, klic });
  });

  // Schůzka z rezervace proběhla před N–oknem dní a klient se dál neposunul → follow-up.
  const p = PRAVIDLA.typy.followup_schuzka;
  pole(tasks).forEach((t) => {
    if (t.source !== "from_booking") return;
    const c = podleId.get(String(t.contact_id));
    if (!c || jeKlient(c)) return;
    const kdy = rozlozDatum(t.start_iso || t.deadline);
    if (!kdy) return;
    const po = dnyMezi(kdy, dnesD);
    if (po >= p.poDnech && po <= p.oknoDni) pridej("followup_schuzka", c, dnesD, { kdy: 0, klic: String(t.id || "") });
  });

  // Ruční událost (např. změna sazeb): poradce zadá datum + text, jde všem klientům.
  const rucni = nastaveni && nastaveni.rucni && typeof nastaveni.rucni === "object" ? nastaveni.rucni : null;
  const rucniDatum = rucni && rucni.aktivni ? rozlozDatum(rucni.datum) : null;
  if (rucniDatum) {
    const dny = dnyMezi(dnesD, rucniDatum);
    if (dny >= -PRAVIDLA.poTerminuDni && dny <= 7) {
      kontakty.filter(jeKlient).forEach((c) => pridej("sazby", c, rucniDatum, { kdy: Math.max(0, dny), text: typeof rucni.text === "string" ? rucni.text : "" }));
    }
  }

  const vaha = { vysoka: 0, stredni: 1, nizka: 2 };
  return out.sort((a, b) => (a.kdy - b.kdy) || ((vaha[a.priorita] ?? 1) - (vaha[b.priorita] ?? 1)) || a.jmeno.localeCompare(b.jmeno, "cs"));
}

/* Počty podle typu pro zvoneček: „Dnes: 3 narozeniny, 1 fixace". */
const NAZVY_POCTU = {
  cs: { narozeniny: ["narozeniny", "narozeniny", "narozenin"], narozeniny_partner: ["narozeniny partnera", "narozeniny partnerů", "narozenin partnerů"], vyroci_spoluprace: ["výročí", "výročí", "výročí"], dite_18: ["dítě 18 let", "děti 18 let", "dětí 18 let"], fixace: ["fixace", "fixace", "fixací"], vyroci_smlouvy: ["výročí smlouvy", "výročí smluv", "výročí smluv"], servis: ["servis", "servisy", "servisů"], followup_schuzka: ["follow-up", "follow-upy", "follow-upů"], lead_bez_odpovedi: ["lead bez odpovědi", "leady bez odpovědi", "leadů bez odpovědi"], dlouho_bez_kontaktu: ["klient dlouho bez kontaktu", "klienti dlouho bez kontaktu", "klientů dlouho bez kontaktu"], sazby: ["ruční zpráva", "ruční zprávy", "ručních zpráv"] },
  sk: { narozeniny: ["narodeniny", "narodeniny", "narodenín"], narozeniny_partner: ["narodeniny partnera", "narodeniny partnerov", "narodenín partnerov"], vyroci_spoluprace: ["výročie", "výročia", "výročí"], dite_18: ["dieťa 18 rokov", "deti 18 rokov", "detí 18 rokov"], fixace: ["fixácia", "fixácie", "fixácií"], vyroci_smlouvy: ["výročie zmluvy", "výročia zmlúv", "výročí zmlúv"], servis: ["servis", "servisy", "servisov"], followup_schuzka: ["follow-up", "follow-upy", "follow-upov"], lead_bez_odpovedi: ["lead bez odpovede", "leady bez odpovede", "leadov bez odpovede"], dlouho_bez_kontaktu: ["klient dlho bez kontaktu", "klienti dlho bez kontaktu", "klientov dlho bez kontaktu"], sazby: ["ručná správa", "ručné správy", "ručných správ"] },
};

/** „Dnes: 3 narozeniny, 1 fixace" — jen události na dnes; prázdný řetězec, když nic. */
export function shrnutiDne(udalosti, lang = "cs") {
  const N = NAZVY_POCTU[lang] || NAZVY_POCTU.cs;
  const pocty = {};
  pole(udalosti).forEach((u) => { if (u.kdy <= 0 && N[u.typ]) pocty[u.typ] = (pocty[u.typ] || 0) + 1; });
  const casti = Object.keys(N).filter((t) => pocty[t]).map((t) => {
    const n = pocty[t];
    const tvar = n === 1 ? N[t][0] : (n >= 2 && n <= 4 ? N[t][1] : N[t][2]);
    return `${n} ${tvar}`;
  });
  if (!casti.length) return "";
  return `Dnes: ${casti.join(", ")}`;
}
